import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["messages", "form", "input", "submit", "conversationId", "newChatBtn"]
  
  connect() {
    this.scrollToBottom()
  }
  
  sendMessage(event) {
    event.preventDefault()
    
    const message = this.inputTarget.value.trim()
    if (!message) return
    
    // Add user message immediately
    this.addMessage(message, 'user')
    this.inputTarget.value = ''
    
    // Show typing indicator
    this.showTyping()
    
    // Disable send button
    this.submitTarget.disabled = true
    
    // Prepare form data
    const formData = new FormData()
    formData.append('message', message)
    if (this.conversationIdTarget.value) {
      formData.append('conversation_id', this.conversationIdTarget.value)
    }
    
    // Send to server
    fetch('/chat/messages', {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.hideTyping()
      
      if (data.error) {
        this.addMessage(data.error, 'assistant')
      } else {
        this.addMessage(data.ai_message.content, 'assistant', data.ai_message.sources)
        this.conversationIdTarget.value = data.conversation_id
      }
    })
    .catch(error => {
      this.hideTyping()
      this.addMessage('Sorry, I encountered an error. Please try again.', 'assistant')
      console.error('Chat error:', error)
    })
    .finally(() => {
      this.submitTarget.disabled = false
    })
  }
  
  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage(event)
    }
  }
  
  selectConversation(event) {
    const conversationId = event.currentTarget.dataset.conversationId
    window.location.href = `/chat/${conversationId}`
  }
  
  newConversation(event) {
    event.preventDefault()
    this.conversationIdTarget.value = ''
    this.messagesTarget.innerHTML = `
      <div class="text-center text-muted mt-5">
        <i class="fas fa-robot fa-3x mb-3"></i>
        <h4>New conversation started!</h4>
        <p class="text-muted">What would you like to know?</p>
      </div>
    `
  }
  
  addMessage(content, role, sources = []) {
    const messageDiv = document.createElement('div')
    messageDiv.className = `message mb-3 ${role}`
    
    if (role === 'user') {
      messageDiv.innerHTML = `
        <div class="d-flex justify-content-end">
          <div class="bg-primary dark p-2 rounded" style="max-width: 70%;">
            ${this.formatContent(content)}
          </div>
        </div>
      `
    } else {
      let sourcesHtml = ''
      if (sources && sources.length > 0) {
        sourcesHtml = `
          <div class="message-sources mt-2 pt-2 border-top">
            <small class="text-muted"><strong>Sources:</strong></small>
            ${sources.map(source => `
              <br><small>
                <a href="${source.url}" target="_blank" class="text-decoration-none">
                  <i class="fas fa-external-link-alt"></i> ${source.title}
                </a>
              </small>
            `).join('')}
          </div>
        `
      }
      
      messageDiv.innerHTML = `
        <div class="d-flex justify-content-start">
          <div class="bg-light p-2 rounded text-dark" style="max-width: 80%;">
            <div class="message-content">
              ${this.formatContent(content)}
            </div>
            ${sourcesHtml}
          </div>
        </div>
      `
    }
    
    this.messagesTarget.appendChild(messageDiv)
    this.scrollToBottom()
  }
  
  showTyping() {
    const typingDiv = document.createElement('div')
    typingDiv.id = 'typing-indicator'
    typingDiv.className = 'message mb-3 assistant'
    typingDiv.innerHTML = `
      <div class="d-flex justify-content-start">
        <div class="bg-light p-2 rounded">
          <div class="typing-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    `
    this.messagesTarget.appendChild(typingDiv)
    this.scrollToBottom()
  }
  
  hideTyping() {
    const typingIndicator = document.getElementById('typing-indicator')
    if (typingIndicator) {
      typingIndicator.remove()
    }
  }
  
  formatContent(content) {
    // First convert newlines to <br>
    let formatted = content.replace(/\n/g, '<br>')
    
    // Then linkify /portal URLs
    formatted = formatted.replace(/(\/portal\?search_query=[^\s<]+)/g, (url) => {
      const decodedUrl = decodeURIComponent(url)
      return `<a href="${url}" class="text-primary text-decoration-none fw-bold" target="_blank">
        <i class="fas fa-external-link-alt me-1"></i>${decodedUrl}
      </a>`
    })
    
    return formatted
  }
  
  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}
